import { useState } from 'react';
import {
    Box, Button, Card, CardContent, CircularProgress, IconButton, Stack, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchSeoInsights } from './seoApi';
import { buildInsightPayload } from './seoInsightsCompute';
import { SeoReport } from './seoTypes';

/** Render the LLM's lightweight markdown (bold + bullets) — same as /analytics. */
const renderMarkdown = (text: string): string =>
    text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/^- /gm, '&bull; ');

/**
 * AI organic-insights action brief — a direct analog of the Analytics
 * "Executive Summary" card. Builds a compact payload from the loaded report and
 * the selected month, posts it to /seo-report/insights, and renders the brief.
 */
export const SeoExecSummary = ({ report, sel }: { report: SeoReport; sel: number }) => {
    const [summary, setSummary] = useState('');
    const [loading, setLoading] = useState(false);

    const generate = async () => {
        setLoading(true);
        try {
            const payload = buildInsightPayload(report, sel);
            const result = await fetchSeoInsights(report.months[sel], payload);
            setSummary(result);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to generate SEO insights:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card sx={{ mb: 2, borderLeft: '4px solid #1565c0' }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AutoAwesomeIcon fontSize="small" color="primary" />
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Organic Insights
                    </Typography>
                    {summary && !loading && (
                        <IconButton size="small" onClick={() => void generate()} title="Regenerate">
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    )}
                </Stack>

                {!summary && !loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                            Generate an AI action brief — top movers, pages to rewrite, keyword gaps, and
                            paid-search moves — from this month&apos;s organic data.
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => void generate()}
                        >
                            Generate
                        </Button>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {summary && !loading && (
                    <Typography
                        variant="body1"
                        sx={{ whiteSpace: 'pre-wrap', '& strong': { fontWeight: 700 }, lineHeight: 1.7 }}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(summary) }}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default SeoExecSummary;
